import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Typography } from "@mui/material";
import { numberDecimalFormat } from "src/utils/number-format";
import { Tabs, Tab } from "@mui/material";
import { useState } from "react";
import DataSourceContext from "../context/DataSourceMngContext";
import { useContext, useMemo, useEffect } from "react";


export const HuaweiContent = ({ selectedStation, setSelectedStation }) => {
  const { fetchHistoryDetail, history, self_upload_type, historyDetail, selectServer } = useContext(DataSourceContext);
  const [stationData, setStationData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState('day_use_energy');
  const [hoveredBar, setHoveredBar] = useState(null);

  const _stations = useMemo(() => {
    if (!history || !Array.isArray(history)) return [];
    
    if (selectServer?.code === 'huawei') {
      return history.map((item) => ({
        id: item?.id,
        station_name: item?.station_name,
        station_code: item?.station_code,
        status: item?.status,
      }));
    }
    
    if (self_upload_type === 'water_meter') {
      return history.flatMap((historyItem) => 
        historyItem?.items?.map((item) => ({
          id: item?.id,
          station_name: item?.serial_number,
          station_code: item?.serial_number,
        })) || []
      );
    }
    
    // Default
    return history.map((item) => ({
      id: item?.id,
      station_name: item?.station_name,
      station_code: item?.station_code,
      status: item?.status,
    }));
  }, [history, self_upload_type, selectServer?.code]);


  useEffect(() => {
    if (_stations && _stations.length > 0 && !selectedStation) {
      setSelectedStation(_stations[0]);
      fetchHistoryDetail(_stations[0]?.id);
    }
  }, [_stations, selectedStation]);

  useEffect(() => {
    if (selectServer?.code === 'huawei') {
      setStationData(historyDetail);
    }
  }, [historyDetail]);

  const metricOptions = [
    { value: 'day_power', label: 'Energy (kWh)' },
    { value: 'day_use_energy', label: 'Energy Consumption (kWh)' },
    { value: 'day_on_grid_energy', label: 'Grid Energy (kWh)' },
    { value: 'month_power', label: 'Monthly Energy (kWh)' },
    { value: 'total_power', label: 'Total Energy (kWh)' },
  ];

  const handleStationChange = (value) => {
    setSelectedStation(value);
    if (value) {
      fetchHistoryDetail(value?.id);
    } else {
      setStationData(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box
        sx={{
          mb: 1,
          width: "50%",
          maxWidth: "400px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          mt: -6,
          zIndex: 10
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ minWidth: "fit-content" }}
        >
          Select Station
        </Typography>
        <Autocomplete
          disableClearable
          options={_stations}
          getOptionLabel={(option) => option?.station_name || ""}
          onChange={(_, newValue) => handleStationChange(newValue)}
          value={selectedStation}
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select station"
              sx={{
                ".MuiInputBase-root": {
                  "& > .MuiInputBase-input": {
                    padding: "8px",
                  },
                },
                ".MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box key={`station-${option?.id}`} {...props}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                {option.station_name}
              </Typography>
            </Box>
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
        />
      </Box>

      <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
        {stationData != null ? (
          <Box sx={{ height: "fit-content", p: 2 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
            >
              {stationData.station_name}
            </Typography>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Table" />
                <Tab label="Chart" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              <Box sx={{ overflowY: "auto", maxHeight: "400px" }}>
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                      backgroundColor: "#f5f5f5",
                      borderBottom: "1px solid #e0e0e0",
                      p: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "center" }}
                    >
                      Date
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "right" }}
                    >
                      Energy (kWh)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "right" }}
                    >
                      Energy Consumption (kWh)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "right" }}
                    >
                      Grid Energy (kWh)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "right " }}
                    >
                      Monthly Energy (kWh)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", textAlign: "right" }}
                    >
                      Total Energy (kWh)
                    </Typography>
                  </Box>
                  {stationData.data_day?.map((day, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                        borderBottom: index < 9 ? "1px solid #f0f0f0" : "none",
                        p: 1,
                        "&:hover": { backgroundColor: "#f8f9fa" },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ textAlign: "center" }}
                      >
                        {day.date}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {numberDecimalFormat(day.day_power, 2)}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {numberDecimalFormat(day.day_use_energy, 2)}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {numberDecimalFormat(day.day_on_grid_energy, 2)}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {numberDecimalFormat(day.month_power, 2)}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {numberDecimalFormat(day.total_power, 2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Graph Tab Content */}
            {activeTab === 1 && (
              <Box
                sx={{
                  height: "450px",
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                {/* Metric Selector */}
                <Box
                  sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Typography variant="body2" sx={{ minWidth: "fit-content" }}>
                    Select Metric:
                  </Typography>
                  <Autocomplete
                    options={metricOptions}
                    getOptionLabel={(option) => option.label}
                    value={metricOptions.find(
                      (option) => option.value === selectedMetric
                    )}
                    onChange={(_, newValue) =>
                      setSelectedMetric(newValue?.value || "day_use_energy")
                    }
                    sx={{ width: "300px" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select metric"
                        size="small"
                      />
                    )}
                  />
                </Box>

                {/* Chart Container with Y-axis */}
                <Box sx={{ display: "flex", height: "300px" }}>
                  {/* Y-axis */}
                  <Box
                    sx={{
                      width: "60px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      pr: 1,
                    }}
                  >
                    {(() => {
                      const chartData = stationData.data_day?.slice(-30);
                      const maxValue = Math.max(
                        ...chartData.map((d) => d[selectedMetric] || 0)
                      );

                      // Create numbers for Y-axis
                      const getNiceTicks = (max) => {
                        if (max === 0) return [0, 1, 2, 3];

                        const magnitude = Math.pow(
                          10,
                          Math.floor(Math.log10(max))
                        );
                        const normalized = max / magnitude;

                        let step;
                        if (normalized <= 1) step = 0.2;
                        else if (normalized <= 2) step = 0.5;
                        else if (normalized <= 4) step = 1;
                        else step = 2;

                        const stepValue = step * magnitude;
                        const ticks = [];
                        for (let i = 0; i <= 5; i++) {
                          ticks.push(i * stepValue);
                        }
                        return ticks;
                      };

                      const ticks = getNiceTicks(maxValue);

                      return ticks.reverse().map((value, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "20px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "10px",
                              color: "textSecondary",
                              textAlign: "right",
                              width: "100%",
                            }}
                          >
                            {numberDecimalFormat(value, value < 1 ? 1 : 0)}
                          </Typography>
                        </Box>
                      ));
                    })()}
                  </Box>

                  {/* Y-axis label */}
                  <Box
                    sx={{
                      width: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: "textSecondary",
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                        transform: "rotate(180deg)",
                      }}
                    >
                      {
                        metricOptions.find(
                          (opt) => opt.value === selectedMetric
                        )?.label
                      }
                    </Typography>
                  </Box>

                  {/* Chart Area */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "end",
                      justifyContent: "center",
                      gap: 1,
                      px: 2,
                      overflowX: "auto",
                      position: "relative",
                      minHeight: "250px",
                    }}
                  >
                    {/* Grid Lines */}
                    {(() => {
                      const chartData = stationData.data_day?.slice(-30);
                      const maxValue = Math.max(
                        ...chartData.map((d) => d[selectedMetric] || 0)
                      );

                      const getNiceTicks = (max) => {
                        if (max === 0) return [0, 1, 2, 3];

                        const magnitude = Math.pow(
                          10,
                          Math.floor(Math.log10(max))
                        );
                        const normalized = max / magnitude;

                        let step;
                        if (normalized <= 1) step = 0.2;
                        else if (normalized <= 2) step = 0.5;
                        else if (normalized <= 4) step = 1;
                        else step = 2;

                        const stepValue = step * magnitude;
                        const ticks = [];
                        for (let i = 0; i <= 5; i++) {
                          ticks.push(i * stepValue);
                        }
                        return ticks;
                      };

                      const ticks = getNiceTicks(maxValue);
                      const chartMax = ticks[ticks.length - 1];

                      return ticks.map((value, index) => {
                        const ratio = chartMax > 0 ? value / chartMax : 0;
                        return (
                          <Box
                            key={`grid-${index}`}
                            sx={{
                              position: "absolute",
                              top: `${ratio * 100}%`,
                              left: 0,
                              right: 0,
                              height: "1px",
                              backgroundColor: "#e0e0e0",
                              borderStyle: "dashed",
                              zIndex: 0,
                            }}
                          />
                        );
                      });
                    })()}

                    {/* Bars */}
                    {stationData.data_day?.slice(-30).map((day, index) => {
                      const chartData = stationData.data_day?.slice(-30);
                      const maxValue = Math.max(
                        ...chartData.map((d) => d[selectedMetric] || 0)
                      );

                      // Get the same nice ticks for height calculation
                      const getNiceTicks = (max) => {
                        if (max === 0) return [0, 1, 2, 3, 4];

                        const magnitude = Math.pow(
                          10,
                          Math.floor(Math.log10(max))
                        );
                        const normalized = max / magnitude;

                        let step;
                        if (normalized <= 1) step = 0.2;
                        else if (normalized <= 2) step = 0.5;
                        else if (normalized <= 5) step = 1;
                        else step = 2;

                        const stepValue = step * magnitude;
                        const ticks = [];
                        for (let i = 0; i <= 5; i++) {
                          ticks.push(i * stepValue);
                        }
                        return ticks;
                      };

                      const ticks = getNiceTicks(maxValue);
                      const chartMax = ticks[ticks.length - 1];
                      const height =
                        chartMax > 0
                          ? ((day[selectedMetric] || 0) / chartMax) * 200
                          : 4;

                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: "40px",
                            flex: "0 0 auto",
                            // zIndex: 0,
                            position: "relative",
                          }}
                        >
                          {/* Bar */}
                          <Box
                            sx={{
                              width: "24px",
                              height: `${height}px`,
                              backgroundColor: "#3dbb89",
                              minHeight: "4px",
                              transition: "all 0.3s ease",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#2a9d6f",
                                transform: "scale(1.05)",
                                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                              },
                            }}
                            onMouseEnter={() =>
                              setHoveredBar({
                                index,
                                day,
                                value: day[selectedMetric],
                              })
                            }
                            onMouseLeave={() => setHoveredBar(null)}
                          />

                          {/* Tooltip */}
                          {hoveredBar?.index === index && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "-80px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "rgba(0,0,0,0.9)",
                                color: "white",
                                padding: "8px 12px",
                                borderRadius: "8px",
                                fontSize: "11px",
                                zIndex: 99999,
                                whiteSpace: "nowrap",
                                boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                minWidth: "120px",
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  display: "block",
                                }}
                              >
                                {day.date
                                  ? (() => {
                                      const date = new Date(day.date);
                                      const dayNum = date.getDate();
                                      const monthNames = [
                                        "Jan",
                                        "Feb",
                                        "Mar",
                                        "Apr",
                                        "May",
                                        "Jun",
                                        "Jul",
                                        "Aug",
                                        "Sep",
                                        "Oct",
                                        "Nov",
                                        "Dec",
                                      ];
                                      const month = monthNames[date.getMonth()];
                                      return `${dayNum} ${month}`;
                                    })()
                                  : ""}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontSize: "10px",
                                  display: "block",
                                  mt: 0.5,
                                }}
                              >
                                {
                                  metricOptions.find(
                                    (opt) => opt.value === selectedMetric
                                  )?.label
                                }
                                : {numberDecimalFormat(day[selectedMetric], 1)}
                              </Typography>
                            </Box>
                          )}

                          {/* Date */}
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              fontSize: "9px",
                              color: "textSecondary",
                              textAlign: "center",
                              minWidth: "30px",
                              lineHeight: 1.2,
                            }}
                          >
                            {day.date
                              ? (() => {
                                  const date = new Date(day.date);
                                  const dayNum = date.getDate();
                                  const monthNames = [
                                    "Jan",
                                    "Feb",
                                    "Mar",
                                    "Apr",
                                    "May",
                                    "Jun",
                                    "Jul",
                                    "Aug",
                                    "Sep",
                                    "Oct",
                                    "Nov",
                                    "Dec",
                                  ];
                                  const month = monthNames[date.getMonth()];
                                  return `${dayNum} ${month}`;
                                })()
                              : ""}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Chart Legend */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                    gap: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: "#3dbb89",
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption">
                      {
                        metricOptions.find(
                          (opt) => opt.value === selectedMetric
                        )?.label
                      }
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              color: "gray",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              mt: "15%",
            }}
          >
            Please select a station to view data
          </Box>
        )}
      </Box>
    </Box>
  );
};
